import {
  WSAlert,
  WSAvatarProps,
  WSList,
  WSMarginProps,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  SharedFileRequestJob,
  SharedFileRequestJobStatus
} from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";

import { CollapsibleItem } from "../../../../shared/components/CollapsibleItem/CollapsibleItem";
import { RequirementContextType } from "../../types";

// `type` is display-only — besides shared-file jobs the widget renders
// identity-verification checks and insurance-monitor checks, whose kinds are
// not `SharedFileRequestJobType` members
export type ValidationItemProps = Omit<SharedFileRequestJob, "type"> & {
  type?: string;
  warningReason?: string;
  statusSummary?: string;
  groupKey?: string;
};

type Props = {
  item: ValidationItemProps;
  contextType: RequirementContextType;
} & WSMarginProps;

export const ValidationItem: React.FC<Props> = ({
  item,
  contextType,
  ...marginProps
}) => {
  const { title, description } = item;

  // When we display the AI validation widget in the requirement details view as instructions, we will just
  // show the title and description of the job
  const status = item.status || SharedFileRequestJobStatus.CREATED;
  const statusReason = item.statusReason || "";

  const { warningReason } = item;
  const { statusSummary } = item;

  const avatarProps = useMemo(() => {
    const statusToPropsMap: Record<SharedFileRequestJobStatus, WSAvatarProps> =
      {
        [SharedFileRequestJobStatus.CREATED]: {
          type: "icon",
          colorBackground: "gray300",
          color: "gray600",
          icon: "time"
        },
        [SharedFileRequestJobStatus.PENDING]: {
          type: "icon",
          colorBackground: "gray300",
          color: "gray600",
          icon: "time"
        },
        [SharedFileRequestJobStatus.PROCESSING]: {
          type: "icon",
          colorBackground: "gray50",
          color: "blue300",
          icon: "loader"
        },
        [SharedFileRequestJobStatus.COMPLETE]: {
          type: "icon",
          colorBackground: "green400",
          color: "white",
          icon: "check-circle"
        },
        [SharedFileRequestJobStatus.FAILED]: {
          type: "icon",
          colorBackground: "red400",
          color: "white",
          icon: "alert-circle"
        },
        [SharedFileRequestJobStatus.ERROR]: {
          type: "icon",
          colorBackground: "red400",
          color: "white",
          icon: "alert-circle"
        } // TODO: need to differentiate between Error and Failed
      };

    return statusToPropsMap[status];
  }, [status]);

  // expand the item if status is Failed OR Error OR Complete with warnings
  const expanded =
    status === SharedFileRequestJobStatus.FAILED ||
    status === SharedFileRequestJobStatus.ERROR ||
    (status === SharedFileRequestJobStatus.COMPLETE && !!warningReason);

  return (
    <CollapsibleItem
      avatarProps={avatarProps}
      isExpanded={expanded}
      title={title || ""}
      {...marginProps}
      // subtitle={description}
    >
      <WSList gap="M">
        <WSText.ParagraphSm color="gray600" weight="book">
          {statusSummary || description}
        </WSText.ParagraphSm>

        {status === SharedFileRequestJobStatus.COMPLETE && !statusSummary && (
          <WSAlert icon="check-circle" theme="success" title="Passed" />
        )}

        {statusReason && status === SharedFileRequestJobStatus.FAILED && (
          <WSAlert icon="alert-circle" theme="error" title="Failed">
            {statusReason}
          </WSAlert>
        )}
        {warningReason && (
          <WSAlert icon="alert" theme="warning" title="Risk signals">
            {warningReason}
          </WSAlert>
        )}
      </WSList>
    </CollapsibleItem>
  );
};
